/***
 **Module Name: RefundHistory
 **File Name :  RefundHistory.jsx
 **Project :  SPOT SAVER
 **Copyright(c) : SPOT SAVER.
 **Organization : SPOT SAVER
 **author :  Rajesh
 **license :
 **version :  1.0.0
 **Created on : Oct 08 2026
 **Last modified on:
 **Description : The refunds of one visitor payment, opened under its row on
 **              Finance > Transactions > Visitors: when, how much, who paid for it,
 **              the reason, and the note.
 **
 **              Reads the row's refunds[], which the API (models/MongoDB/bookingRefund.js)
 **              appends one entry to per refund. The note is the admin's own words from
 **              the Refund window; a refund made in the Stripe Dashboard gets a fixed note
 **              from the charge.refunded sync instead. Admin-only: the Refund window tells
 **              the admin the note is not sent to anyone, so nothing here reaches the venue.
 ***/
import React from "react";
import moment from "moment";
import { REASONS } from "./RefundBookingModal";
import { HEADING, MUTED, BORDER, PANEL } from "./stripeStatusMeta";
import { money } from "../revenueFilters";

// bookingRefund.js stamps this as refundedBy on refunds made in the Stripe Dashboard;
// every other refund carries the admin's userid.
const STRIPE_DASHBOARD = "stripe-dashboard";

const toNum = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : 0;
};

/** The row's refunds, newest first. */
export const refundsOf = (row) =>
    (Array.isArray(row?.refunds) ? row.refunds : [])
        .slice()
        .sort((a, b) => new Date(b?.created || 0) - new Date(a?.created || 0));

const reasonLabel = (reason) => {
    if (!reason) return "";
    const known = REASONS.find((r) => r.value === reason);
    return known ? known.label : String(reason).replace(/_/g, " ");
};

const whoPaid = (refund) => {
    const reversed = toNum(refund?.venueShareReversed);
    if (reversed > 0) return `${money(reversed)} taken back from the venue`;
    return refund?.venuePays ? "Venue's share taken back" : "SpotSaver covered it";
};

const RefundHistory = ({ data: row }) => {
    const refunds = refundsOf(row);

    return (
        <div
            className="px-4 py-3"
            id={`refund-history-${row?.transactionId}`}
            style={{ background: PANEL, borderTop: `1px solid ${BORDER}`, borderBottom: `1px solid ${BORDER}` }}
        >
            <div style={{ color: HEADING, fontSize: 14, fontWeight: 700 }} className="mb-1">
                {refunds.length > 1 ? `Refunds (${refunds.length})` : "Refund"}
            </div>

            {refunds.length === 0 && (
                // Refunded by the old /refund route, before refunds[] existed.
                <div style={{ color: MUTED, fontSize: 13 }}>
                    This payment was refunded before refund notes were kept, so there is no note to show.
                </div>
            )}

            {refunds.map((r, i) => (
                <div
                    key={r?.refundId || i}
                    className="py-2"
                    id={`refund-entry-${r?.refundId || i}`}
                    style={{ borderTop: i ? `1px solid ${BORDER}` : "none" }}
                >
                    <div className="d-flex flex-wrap align-items-baseline" style={{ columnGap: 16, rowGap: 2, color: MUTED, fontSize: 13 }}>
                        <strong style={{ color: HEADING, fontSize: 14 }}>{money(r?.amount)}</strong>
                        {r?.created && <span>{moment(r.created).format("MM-DD-YYYY h:mm A")}</span>}
                        <span>{whoPaid(r)}</span>
                        {r?.reason && <span>{reasonLabel(r.reason)}</span>}
                        <span>{r?.refundedBy === STRIPE_DASHBOARD ? "Stripe Dashboard" : "SpotSaver admin"}</span>
                    </div>
                    <div className="mt-1" style={{ color: HEADING, fontSize: 14, whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                        {r?.note || <em style={{ color: MUTED }}>No note</em>}
                    </div>
                </div>
            ))}
        </div>
    );
};

export default RefundHistory;
